/**
 * @file usePointsRechargeTracking - 积分充值弹窗打点 Hook
 * @description 自动注入 page 与会话级公参
 *              （member_level / subscription_type / is_first_charge / scene_key），
 *              调用点仅需传递 value / 差异化 ext（如 package_id）。
 */

import {useCallback, useMemo} from 'react';
import type {PointsSku} from '@/services/commerce/points/interface';
import type {LogParams} from '@/services/tracking';
import {useTracking} from '@/services/tracking/context';
import {TrackEvents} from '@/services/tracking/constant';
import {useMemberLogExt} from '@/hooks/memberLogExt';
import {useModalStore} from '@/store/useModalStore';

interface UsePointsRechargeTrackingOptions {
    /** 已加载的积分 SKU 列表，用于派生 is_first_charge */
    skuList?: PointsSku[];
}

/** 首充用户：SKU 列表非空且全部处于 isFirstCharge === true */
const isFirstChargeUser = (skus?: PointsSku[]): boolean =>
    !!skus && skus.length > 0 && skus.every((sku) => sku.isFirstCharge);

export const usePointsRechargeTracking = (options?: UsePointsRechargeTrackingOptions) => {
    const {sendClickLog, sendShowLog} = useTracking();
    const memberLogExt = useMemberLogExt();
    const source = useModalStore((s) => s.pointsRechargeModalSource);
    const {skuList} = options ?? {};

    // 会话级公参（package_id 属事件粒度，由调用点自行传入）
    const baseExt = useMemo(
        () => ({
            ...memberLogExt,
            /* eslint-disable camelcase */
            is_first_charge: isFirstChargeUser(skuList),
            ...(source && {scene_key: source}),
            /* eslint-enable camelcase */
        }),
        [memberLogExt, skuList, source],
    );

    // 包装打点方法：自动注入 page + 公参
    const wrappedSendClickLog = useCallback(
        (params: Omit<LogParams, 'type'>) => {
            sendClickLog({
                page: TrackEvents.POINT_CREDITS_PURCHASE_POP,
                ...params,
                ext: {
                    ...baseExt,
                    ...params.ext,
                },
            });
        },
        [sendClickLog, baseExt],
    );

    const wrappedSendShowLog = useCallback(
        (params: Omit<LogParams, 'type'>) => {
            sendShowLog({
                page: TrackEvents.POINT_CREDITS_PURCHASE_POP,
                ...params,
                ext: {
                    ...baseExt,
                    ...params.ext,
                },
            });
        },
        [sendShowLog, baseExt],
    );

    return {
        sendClickLog: wrappedSendClickLog,
        sendShowLog: wrappedSendShowLog,
        source,
    };
};
